import React, { useState, useEffect } from "react";
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Paper,
  CircularProgress,
  Divider,
  Alert,
} from "@mui/material";
import {
  LocalShipping as ShippingIcon,
  Receipt as BillingIcon,
  Assessment as ReportIcon,
  Check as CheckIcon,
} from "@mui/icons-material";
import { NotificationManager } from "react-notifications";
import { useHistory } from "react-router-dom";
import { getClientDetails, updateClientDetails } from "../../services/clientService";
import CommonLoader from "../common/CommonLoader";

import ShippingTab from "./ShippingTab";
import BillingTab from "./BillingTab";
import ReportTab from "./ReportTab";
import PageHeading from "../common/PageHeading";

const STEPS = [
  { label: "Shipping", icon: <ShippingIcon /> },
  { label: "Billing", icon: <BillingIcon /> },
  { label: "Report", icon: <ReportIcon /> },
];

export default function Client({ accountNumber }) {
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [allDone, setAllDone] = useState(false);
  const history = useHistory();

  useEffect(() => {
    async function fetchClient() {
      try {
        const data = await getClientDetails(accountNumber);
        setFormData(data);
      } catch (err) {
        console.error(err);
        NotificationManager.error("Failed to load client data.");
      } finally {
        setLoading(false);
      }
    }
    if (accountNumber) fetchClient();
  }, [accountNumber]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name.includes("Type") ? +value : value,
    }));
  };

  const handleNext = () => setActiveStep((s) => s + 1);
  const handleBack = () => setActiveStep((s) => s - 1);
  const handleJump = (step) => setActiveStep(step);

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await updateClientDetails(formData);
      if (response) {
        setAllDone(true);
        NotificationManager.success("Client profile updated successfully.");
        setTimeout(() => history.push("/dashboard"), 1500);
      } else {
        NotificationManager.error("Error while updating data.");
      }
    } catch (err) {
      NotificationManager.error("Error while updating data.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <CommonLoader loading={loading} />
    );
  }

  if (!formData) {
    return (
      <Box p={3}>
        <Alert severity="error">Unable to load client profile.</Alert>
      </Box>
    );
  }

  const isLast = activeStep === STEPS.length - 1;

  const tabProps = { formData, handleChange, setFormData };

  const stepContent = [
    <ShippingTab key="shipping" {...tabProps} />,
    <BillingTab key="billing"  {...tabProps} />,
    <ReportTab key="report"   {...tabProps} />,
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>

      <Paper elevation={0} variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
        <PageHeading title="Client Profile" />
        <Stepper activeStep={activeStep} alternativeLabel nonLinear>
          {STEPS.map((step, index) => (
            <Step key={step.label} completed={allDone || activeStep > index}>
              <StepLabel
                icon={
                  allDone || activeStep > index
                    ? <CheckIcon fontSize="small" />
                    : step.icon
                }
                onClick={() => handleJump(index)}
                sx={{
                  cursor: "pointer",
                  "& .MuiStepLabel-label": {
                    fontWeight: activeStep === index ? 700 : 400,
                  },
                }}
              >
                {step.label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      <Paper elevation={2} sx={{ p: { xs: 2, md: 3 }, borderRadius: 2, minHeight: 320 }}>
        <Typography variant="h6" color="primary" fontWeight={700} gutterBottom>
          {STEPS[activeStep].label} Information
        </Typography>
        <Divider sx={{ mb: 2.5 }} />

        {stepContent[activeStep]}
      </Paper>

      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
        <Button
          variant="outlined"
          onClick={activeStep === 0 ? () => history.push("/dashboard") : handleBack}
          disabled={saving}
        >
          {activeStep === 0 ? "Cancel" : "← Back"}
        </Button>

        <Box display="flex" gap={1}>
          {!isLast && (
            <Button variant="contained" color="primary" onClick={handleNext}>
              Next →
            </Button>
          )}
          {isLast && (
            <Button
              variant="contained"
              color="success"
              onClick={handleSave}
              disabled={saving}
              startIcon={
                saving
                  ? <CircularProgress size={16} color="inherit" />
                  : <CheckIcon />
              }
            >
              {saving ? "Saving…" : "Save All"}
            </Button>
          )}
        </Box>
      </Box>

      <CommonLoader loading={saving} />
    </Box>
  );
}